import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogContent,
} from "@material-ui/core";
import Select from "react-select";
// import { components } from "react-select";
// import { AiFillCaretDown } from "react-icons/ai";
import chroma from "chroma-js";
import tinycolor from "tinycolor2";
import COLORS from "scss/colors.module.scss";
import ColorsSet from "app/common/utils/ColorsSet";
import ApiClient from "utils/ApiClient";
import {
    ColumnName,
    SavingSpinnerForColumnEditors,
    ActionButtonsForColumnEditors,
    handleClose,
    ErrorMessageViewer,
    DropdownIndicator,
    getColumnDefById
} from "./ColumnEditorFactory";
import { validateDropdownInputsBeforeSaving } from "../RowId/Validations";
import { OptionFormatter } from "../DropDown/DropDownColumnEditor";

const DropdownReusableEditor = ({
    initialValue = "",
    open,
    data,
    column,
    columnName,
    onSave,
    onCancel
}) => {
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [tempAddedOptionValue, setTempAddedOptionValue] = useState(null);
    const [validationError, setValidationError] = useState({ isInvalid: false, message: "" });
    const [isSaving, setIsSaving] = useState(false);

    const selectRef = useRef(null);
    const newOptionColorRef = useRef(ColorsSet[Math.floor(Math.random() * ColorsSet.length)]);

    const columnOptions = column?.normalizedColumnOptions ?? {};
    const isAdmin = window.appStore.containerStore.listConfig.listPermissions.is_admin == "1";

    const getColorForOption = (isNew, index) => {
        const luminance = tinycolor(isNew ? newOptionColorRef.current : columnOptions.colors?.[index]).getLuminance();
        return luminance > 0.5 ? tinycolor(isNew ? newOptionColorRef.current : columnOptions.colors[index]).darken(60).toString() : COLORS.PRIMARY_COLOR;
    };

    const buildOptionsListOnLoad = () => {
        const opts = [{
            value: "",
            label: "",
            color: COLORS.PRIMARY_COLOR,
            background: "transparent",
        }];
        columnOptions.options?.forEach((option, index) => {
            opts.push({
                value: option,
                label: option,
                color: getColorForOption(false, index),
                background: columnOptions.colors?.[index] || "grey",
            });
        });

        return opts;
    };

    const findAndSetSelectedOption = (opts, value) => {
        const match = opts.find((o) => o.label === value);
        if (match) {
            setSelectedOption(match);
        } else if (value) {
            setSelectedOption({
                value: value,
                label: value,
                color: COLORS.PRIMARY_COLOR,
                background: "grey"
            });
        }
    }

    useEffect(() => {
        if (open) {
            const opts = buildOptionsListOnLoad();
            setOptions(opts);

            if (!initialValue) {
                setSelectedOption(null);
            } else {
                findAndSetSelectedOption(opts, initialValue);
            }
            newOptionColorRef.current = ColorsSet[Math.floor(Math.random() * ColorsSet.length)];
        }
        return () => {
            newOptionColorRef.current = null;
            setValidationError({ isInvalid: false, message: "" });
        };
    }, [open, initialValue]);

    // const DropdownIndicator = (props) => {
    //     return (
    //         <components.DropdownIndicator {...props}>
    //             <AiFillCaretDown size={16} style={{ color: "#555" }} />
    //         </components.DropdownIndicator>
    //     );
    // };

    const handleChange = (selected) => {
        if (!selected) return;

        if (
            columnOptions.allow_free_text &&
            !selected.isNew &&
            !options.some((o) => o.value === selected.value)
        ) {
            setSelectedOption(selected);
            return;
        }

        if (selected.isNew) {
            const newOpt = {
                value: inputValue,
                label: inputValue,
                isNew: true,
                color: getColorForOption(true),
                background: newOptionColorRef.current
            };
            setTempAddedOptionValue(newOpt);
            setSelectedOption(newOpt);
        } else {
            setSelectedOption(selected);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            if (selectRef?.current?.select?.state?.focusedOption 
                && selectRef?.current?.select?.state?.focusedOption.value 
                && options.some((o) => o.value === selectRef.current.select.state.focusedOption.value)
            ) {
                setSelectedOption(selectRef.current.select.state.focusedOption);
            } else if (columnOptions.allow_free_text && inputValue.trim() !== "") {
                e.preventDefault();
                const freeText = {
                    value: inputValue,
                    label: inputValue,
                    color: COLORS.PRIMARY_COLOR,
                    background: "grey",
                    isFreeText: true
                };
                setTempAddedOptionValue(null);
                setSelectedOption(freeText);
                requestAnimationFrame(() => {
                    const inst = selectRef.current;
                    if (!inst) return;
                    if (typeof inst.blur === "function") inst.blur();
                    else if (inst.select && typeof inst.select.blur === "function")
                    inst.select.blur();
                });
            } else {
                e.preventDefault();
            }
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const sel = selectedOption;
            if (!sel || (sel.value === initialValue)) {
                onCancel();
                return;
            }

            const isUpdateAllowed = validateDropdownInputsBeforeSaving(sel.value, column, data);
            if (isUpdateAllowed.isInvalid) {
                setValidationError(isUpdateAllowed);
                return;
            }

            if (sel.isFreeText) {
                await onSave(sel.value, initialValue, column);
            } else if (sel?.isNew && !sel.isFreeText) {
                const { value } = sel;
                const isNewOption = !columnOptions.options.includes(value);

                if (isNewOption && value.trim() !== "" && (isAdmin || columnOptions?.is_allow_non_admin === true)) {
                    columnOptions.options.push(value);
                    columnOptions.colors.push(sel.background);
                    const columnDef = getColumnDefById(column.id);
                    columnDef.cellRendererParams?.columnOptions.options.push(value);
                    columnDef.cellRendererParams?.columnOptions.colors.push(sel.background);

                    try {
                        const resp = await ApiClient.List.addDropdownOption({
                            list_id: column?.list_id,
                            data: JSON.stringify([columnOptions]),
                            columnId: column?.id
                        });

                        if (resp?.data?.result?.column) {
                            window.appStore?.containerStore?.addDropdownOption(resp?.data?.result?.column);
                        }
                    } catch (e) {
                        console.error("Error adding dropdown option:", e);
                    }
                }
                await onSave(value, initialValue, column);
            } else {
                await onSave(sel.value, initialValue, column);
            }
        } catch (err) {
            console.error("Error saving dropdown value:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const selectStyles = {
        control: (provided) => ({
            ...provided,
            minWidth: "100%",
            marginTop: 8
        }),
        menuPortal: base => ({
            ...base,
            zIndex: 2000,
        }),
        menu: base => ({
            ...base,
            zIndex: 2000,
            maxHeight: "250px",
            overflowY: "auto",
        }),
        option: (styles, { data, isDisabled, isFocused, isSelected }) => {
            const background =
                data.background === "transparent" || Array.isArray(data.background)
                ? chroma("grey")
                : chroma(data.background);
            return {
                ...styles,
                display: "flex",
                alignItems: "center",
                backgroundColor: isDisabled
                ? null
                : isSelected || isFocused
                    ? background.alpha(0.1).css()
                    : null,
                color: isDisabled
                ? COLORS.DISABLED_MENU_OPTION
                : isSelected
                    ? chroma.contrast(background, COLORS.PRIMARY_COLOR) > 2
                    ? COLORS.PRIMARY_COLOR
                    : COLORS.SECONDARY_COLOR
                    : data.background,
                cursor: isDisabled ? "not-allowed" : "default",

                ":active": {
                ...styles[":active"],
                backgroundColor:
                    !isDisabled &&
                    (isSelected ? data.background : background.alpha(0.3).css()),
                },
            };
        },
    };

    return (
        <Dialog
            open={open}
            onClose={() => handleClose(selectedOption?.value, initialValue, onCancel, validationError, setValidationError)}
            fullWidth
            PaperProps={{
                style: {
                    width: validationError.isInvalid ? "450px" : "420px",
                    height: validationError.isInvalid ? "200px" : "180px",
                    borderRadius: "8px"
                }
            }}
        >
            <DialogContent>
                <Box display="flex" flexDirection="column" style={{ width: "100%", gap: 8 }}>
                    {columnName && <ColumnName columnName={columnName} />}

                    <Select
                        ref={selectRef}
                        value={selectedOption}
                        onChange={handleChange}
                        onInputChange={(val) => setInputValue(val)}
                        options={[
                            ...options,
                            ...(tempAddedOptionValue ? [tempAddedOptionValue] : []),
                            ...(inputValue &&
                            (isAdmin || columnOptions.is_allow_non_admin === true) &&
                            !options.some((o) => (o.value.toLowerCase() === inputValue.toLowerCase() || (tempAddedOptionValue && tempAddedOptionValue?.value?.toLowerCase() === inputValue.toLowerCase())))
                            ?
                                [
                                    {
                                        value: inputValue,
                                        label: `Add option: ${inputValue}`,
                                        color: getColorForOption(true),
                                        background: newOptionColorRef.current,
                                        isNew: true,
                                        isFreeText: columnOptions?.allow_free_text
                                    },
                                ]
                            : []),
                        ]}
                        isClearable={false}
                        placeholder="Find an option..."
                        styles={selectStyles}
                        components={{
                            DropdownIndicator: DropdownIndicator,
                            IndicatorSeparator: null,
                        }}
                        formatOptionLabel={(props) => (
                            <OptionFormatter
                                {...props}
                                inputValue={inputValue}
                                columnWidth={validationError.isInvalid ? "402px" : "372px"}
                            />
                        )}
                        maxMenuHeight={150}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        onKeyDown={handleKeyDown}
                    />

                    {validationError.isInvalid && (<ErrorMessageViewer errMsg={validationError.message} />)}

                    <ActionButtonsForColumnEditors
                        handleSave={handleSave}
                        handleCancel={() => handleClose(selectedOption?.value, initialValue, onCancel, validationError, setValidationError)}
                        isSaveDisabled={isSaving}
                        isCloseDisabled={isSaving}
                    />

                    {isSaving && (<SavingSpinnerForColumnEditors />)}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default DropdownReusableEditor;
